import { useState, useEffect, useCallback, useRef } from 'react';
import { differenceInMinutes } from 'date-fns';
import type {
  AcuitySlotConfig,
  AcuityReservation,
  AcuityMachineState,
  AcuitySlotsHandle,
  ReleaseWarningPayload,
} from './acuityTypes';
import type { EmbeddedToHostMessage, DateRange } from '../types';
import type { SlotOpenedPayload } from '../waitlist/waitlistTypes';

const AWAITING_CONFIG_TIMEOUT_MS = 5_000;
const AWAITING_RELEASE_TIMEOUT_MS = 120_000;  // 2 minutes
const DEFAULT_WARNING_MINUTES = 30;

export function useAcuitySlots(
  acuityConfig: AcuitySlotConfig | undefined,
  emit: (msg: EmbeddedToHostMessage) => void,
  viewRange: DateRange,
  visibleResourceIds: string[] | undefined,
  onSlotOpened?: (payload: SlotOpenedPayload) => void,
): AcuitySlotsHandle {
  const [machineState, setMachineState] = useState<AcuityMachineState>('IDLE');
  const [reservations, setReservations] = useState<AcuityReservation[]>([]);

  // Track which reservations have had their warning fired this cycle so we
  // don't re-fire it on every 60s tick after it has already been sent.
  const warnedIds = useRef<Set<string>>(new Set());
  // Track reservations in AWAITING_RELEASE_DECISION per reservationId.
  const releaseDecisionTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Stable ref so onReleaseConfirm always sees the latest callback without deps.
  const onSlotOpenedRef = useRef(onSlotOpened);
  useEffect(() => { onSlotOpenedRef.current = onSlotOpened; }, [onSlotOpened]);

  const viewRangeRef = useRef(viewRange);
  useEffect(() => { viewRangeRef.current = viewRange; }, [viewRange]);

  const enabled = acuityConfig?.enabled ?? false;
  const warningWindow = acuityConfig?.releaseWarningMinutes ?? DEFAULT_WARNING_MINUTES;

  // ── Emit ACUITY_SLOTS_REQUEST on range / resource changes ─────────────────

  const prevRangeKeyRef = useRef('');
  useEffect(() => {
    if (!enabled || !acuityConfig) return;

    const resourceIds = visibleResourceIds ?? [];
    const key = `${viewRange.startUtc}|${viewRange.endUtc}|${resourceIds.join(',')}`;
    if (prevRangeKeyRef.current === key) return;
    prevRangeKeyRef.current = key;

    setMachineState('AWAITING_CONFIG');
    warnedIds.current.clear();

    const payload = {
      visibleRangeStart: viewRange.startUtc,
      visibleRangeEnd: viewRange.endUtc,
      resourceIds,
    };
    const result = acuityConfig.onRequestAcuitySlots(payload);
    emit({ type: 'ACUITY_SLOTS_REQUEST', payload });

    // Direct-React mode: callback returns Promise<AcuityReservation[]> — use it immediately.
    if (result && typeof (result as Promise<AcuityReservation[]>).then === 'function') {
      let cancelled = false;
      (result as Promise<AcuityReservation[]>)
        .then((reservations) => {
          if (!cancelled) {
            warnedIds.current.clear();
            releaseDecisionTimers.current.forEach((t) => clearTimeout(t));
            releaseDecisionTimers.current.clear();
            setReservations(reservations);
            setMachineState('SLOTS_ACTIVE');
          }
        })
        .catch(() => { if (!cancelled) setMachineState('IDLE'); });
      return () => { cancelled = true; };
    }

    // iframe/postMessage mode: 5-second timeout if no ACUITY_SLOTS_CONFIG arrives.
    const timerId = setTimeout(() => {
      setMachineState((prev) => prev === 'AWAITING_CONFIG' ? 'IDLE' : prev);
    }, AWAITING_CONFIG_TIMEOUT_MS);

    return () => clearTimeout(timerId);
  // viewRange object identity changes every render — use string values for stability.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, viewRange.startUtc, viewRange.endUtc, visibleResourceIds?.join(',')]);

  // ── Release countdown timer (ticks every 60s while SLOTS_ACTIVE) ──────────

  useEffect(() => {
    if (machineState !== 'SLOTS_ACTIVE' || !acuityConfig) return;

    const tick = () => {
      const now = new Date();

      setReservations((prev) => {
        const next = prev.filter((r) => {
          if (!r.releaseAt) return true;
          const mins = differenceInMinutes(new Date(r.releaseAt), now);

          // Auto-release: remove from state (no RELEASE_HOLD received in time)
          if (mins <= 0 && !releaseDecisionTimers.current.has(r.id)) {
            warnedIds.current.delete(r.id);
            return false;
          }
          return true;
        });

        // Emit warnings for reservations newly inside the warning window.
        next.forEach((r) => {
          if (!r.releaseAt) return;
          const mins = differenceInMinutes(new Date(r.releaseAt), now);
          if (mins > 0 && mins <= warningWindow && !warnedIds.current.has(r.id)) {
            warnedIds.current.add(r.id);
            const payload: ReleaseWarningPayload = {
              reservationId: r.id,
              resourceId: r.resourceId,
              start: r.start,
              releaseAt: r.releaseAt!,
              minutesRemaining: mins,
            };
            acuityConfig.onReleaseWarning?.(payload);
            emit({ type: 'ACUITY_RELEASE_WARNING', payload });

            // 2-minute decision window; auto-release if no response.
            const timerId = setTimeout(() => {
              releaseDecisionTimers.current.delete(r.id);
              setReservations((rs) => rs.filter((x) => x.id !== r.id));
              warnedIds.current.delete(r.id);
            }, AWAITING_RELEASE_TIMEOUT_MS);
            releaseDecisionTimers.current.set(r.id, timerId);
          }
        });

        return next;
      });
    };

    const id = setInterval(tick, 60_000);
    tick(); // run immediately on activation
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineState, warningWindow]);

  // ── Transitions called by ScheduleContext from message handler ─────────────

  const onConfigReceived = useCallback((incoming: AcuityReservation[]) => {
    warnedIds.current.clear();
    // Clear any pending release-decision timers — fresh config supersedes them.
    releaseDecisionTimers.current.forEach((t) => clearTimeout(t));
    releaseDecisionTimers.current.clear();
    setReservations(incoming);
    setMachineState('SLOTS_ACTIVE');
  }, []);

  const onReleaseConfirm = useCallback((reservationId: string) => {
    const t = releaseDecisionTimers.current.get(reservationId);
    if (t !== undefined) {
      clearTimeout(t);
      releaseDecisionTimers.current.delete(reservationId);
    }
    warnedIds.current.delete(reservationId);

    let released: AcuityReservation | undefined;
    setReservations((prev) => {
      released = prev.find((r) => r.id === reservationId);
      return prev.filter((r) => r.id !== reservationId);
    });

    // Notify waitlist that this acuity slot is now general availability.
    if (released && onSlotOpenedRef.current) {
      const vr = viewRangeRef.current;
      const durationMs = new Date(released.end).getTime() - new Date(released.start).getTime();
      onSlotOpenedRef.current({
        slotId: crypto.randomUUID(),
        resourceId: released.resourceId,
        start: released.start,
        end: released.end,
        duration: Math.round(durationMs / 60000),
        openedBy: 'acuity_release',
        acuityReservationId: reservationId,
        acuityHint: released.acuityLevel,
        visibleRangeStart: vr.startUtc,
        visibleRangeEnd: vr.endUtc,
      });
    }

    setMachineState('SLOTS_ACTIVE');
  }, []);

  const onReleaseHold = useCallback((reservationId: string, newReleaseAt?: string) => {
    const t = releaseDecisionTimers.current.get(reservationId);
    if (t !== undefined) {
      clearTimeout(t);
      releaseDecisionTimers.current.delete(reservationId);
    }
    warnedIds.current.delete(reservationId);
    if (newReleaseAt) {
      setReservations((prev) =>
        prev.map((r) => r.id === reservationId ? { ...r, releaseAt: newReleaseAt } : r),
      );
    }
    setMachineState('SLOTS_ACTIVE');
  }, []);

  return { reservations, machineState, onConfigReceived, onReleaseConfirm, onReleaseHold };
}
